import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link2, Loader2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import {
  gpsDriveAsaasCustomersService,
  type AsaasCustomerSearchItem,
} from "@/services/gpsdrive-asaas-integration.service";

// "Puxar dados do Asaas" (pedido 2026-10-26: cliente sem nada na aba
// CLIENTE mas com cadastro completo no Asaas). Procura por CPF/CNPJ ou nome
// completo, mostra o que o Asaas tem e, ao escolher, vincula o cadastro ao
// usuário (faturamento passa a usar ele) e devolve os dados pra quem abriu
// preencher o formulário — a gravação dos dados continua no Salvar.
export function AsaasCustomerPicker({
  open,
  onOpenChange,
  userId,
  initialQuery,
  currentCustomerId,
  onLinked,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  initialQuery: string;
  currentCustomerId?: string | null;
  onLinked: (customer: AsaasCustomerSearchItem) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AsaasCustomerSearchItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const search = useMutation({
    mutationFn: (q: string) => gpsDriveAsaasCustomersService.search(q),
    onMutate: () => setError(null),
    onSuccess: (rows) => setResults(rows),
    onError: (err) => {
      setResults(null);
      setError(err instanceof GpsDriveApiError ? err.message : "Não foi possível buscar no Asaas.");
    },
  });
  const link = useMutation({
    mutationFn: (c: AsaasCustomerSearchItem) => gpsDriveAsaasCustomersService.link(userId, c.id).then(() => c),
    onMutate: () => setError(null),
    onSuccess: (c) => {
      onLinked(c);
      onOpenChange(false);
    },
    onError: (err) => setError(err instanceof GpsDriveApiError ? err.message : "Não foi possível vincular."),
  });

  useEffect(() => {
    if (!open) return;
    const q = initialQuery.trim();
    setQuery(q);
    setResults(null);
    setError(null);
    if (q) search.mutate(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) search.mutate(query.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Buscar cliente no Asaas</DialogTitle>
          <DialogDescription>
            Digite o CPF/CNPJ completo ou o nome. Ao escolher, o faturamento deste usuário passa a usar esse
            cadastro e os dados são preenchidos na aba Cliente para você conferir e salvar.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex gap-2">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="CPF/CNPJ ou nome completo"
            className="h-10"
          />
          <Button type="submit" disabled={search.isPending || !query.trim()} className="h-10 gap-1.5">
            {search.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Buscar
          </Button>
        </form>
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">{error}</div>
        )}
        <div className="max-h-[55vh] space-y-2 overflow-y-auto">
          {results?.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">Nenhum cliente encontrado no Asaas.</p>
          )}
          {results?.map((c) => {
            const current = c.id === currentCustomerId;
            const takenByOther = !!c.linkedUser && c.linkedUser.id !== userId;
            const address = [
              [c.address, c.addressNumber].filter(Boolean).join(", "),
              c.complement,
              c.province,
              [c.cityName, c.state].filter(Boolean).join("/"),
              c.postalCode && `CEP ${c.postalCode}`,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <div key={c.id} className="rounded-lg border border-border p-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-0.5">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {[c.cpfCnpj && `CPF/CNPJ ${c.cpfCnpj}`, c.mobilePhone || c.phone, c.email].filter(Boolean).join(" · ") ||
                        "Sem documento/contato"}
                    </div>
                    {address && <div className="text-xs text-muted-foreground">{address}</div>}
                    <div className="text-xs text-muted-foreground">
                      {c.paymentsCount != null && `${c.paymentsCount} cobrança(s)`}
                      {c.dateCreated && ` · cadastrado em ${c.dateCreated.split("-").reverse().join("/")}`}
                    </div>
                    {takenByOther && (
                      <div className="text-xs font-medium text-amber-600 dark:text-amber-400">
                        Já vinculado ao usuário "{c.linkedUser!.name}"
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={current ? "outline" : "default"}
                    disabled={takenByOther || link.isPending}
                    onClick={() => link.mutate(c)}
                    className="shrink-0 gap-1.5"
                  >
                    {link.isPending && link.variables?.id === c.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Link2 className="h-3.5 w-3.5" />
                    )}
                    {current ? "Puxar dados" : "Vincular"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
